const QUESTION_KEYS = ['imageUnclear', 'startExam', 'measureEf'];

const QUESTION_LABELS = {
  en: {
    imageUnclear: 'Why is the image unclear?',
    startExam: 'How do I start an exam?',
    measureEf: 'How do I measure EF?',
  },
  cs: {
    imageUnclear: 'Proč je obraz nejasný?',
    startExam: 'Jak zahájím vyšetření?',
    measureEf: 'Jak změřím EF?',
  },
  de: {
    imageUnclear: 'Warum ist das Bild unklar?',
    startExam: 'Wie starte ich eine Untersuchung?',
    measureEf: 'Wie messe ich die EF?',
  },
};

const ANSWERS = {
  en: {
    imageUnclear: `Common reasons the image looks soft or noisy on systems like **Vivid E90**:

- **Probe contact & gel** - too little gel, air under the probe, or lifting off the skin during breathing.
- **Gain & depth** - overall gain too low (dark) or too high (bloomy); depth set deeper than needed (resolution drops).
- **Focus & frequency** - focal zone not at the region of interest; try harmonic imaging if available for cleaner borders.
- **Patient / motion** - breathing, arrhythmia, or poor breath-hold; stabilize the view and use **Clear / Auto** if your preset offers it.
- **Preset** - confirm you are in the correct application/cardiac preset for the exam type.

If one area of the image is OK and another is not, adjust **TGC** sliders for that depth. For persistent artifacts, check probe cable and re-gel.`,
    startExam: `Typical start of a cardiac exam on **Vivid E90** (details follow your site's workflow):

1. **Select the patient / study** from the worklist (or register a new patient).
2. Choose the **cardiac application** and the probe you will use (e.g. phased array).
3. **Prepare the patient** - left lateral position when possible, ECG leads if you use ECG gating.
4. **Pick a protocol or preset** (e.g. adult echo) so depth, scale, and modes match the exam.
5. **Acquire** - place the probe, optimize image, then store loops and measurements per your lab's checklist.

Use your department's **standard views** sequence; the system's **Help** or **protocol** tools (if enabled) list recommended views.`,
    measureEf: `On **Vivid E90**, ejection fraction (EF) is often measured with tools such as **Auto EF**, which uses a **2D speckle-tracking** approach on appropriate left-ventricular views.

**Practical steps (high level):**

1. Acquire a good **apical** (or prescribed) view with clear endocardial border definition.
2. Open the **EF / LV function** quantification tool your preset exposes (e.g. Auto EF).
3. Follow on-screen prompts to define the LV region of interest; let the algorithm track the cycle.
4. Review traced borders and the reported **EF**; repeat acquisition if tracking is poor.

Always follow your institution's measurement policy and the manufacturer's instructions for the software version you run.`,
  },
  cs: {
    imageUnclear: `Časté důvody, proč obraz na systémech jako **Vivid E90** působí měkce nebo zašuměně:

- **Kontakt sondy a gel** - málo gelu, vzduch pod sondou nebo odlepování od kůže při dýchání.
- **Gain a hloubka** - celkový gain je příliš nízký (tmavý obraz) nebo vysoký (přesvětlený); hloubka je nastavena zbytečně hluboko.
- **Fokus a frekvence** - fokální zóna není v oblasti zájmu; pokud je k dispozici, zkuste harmonické zobrazování.
- **Pacient / pohyb** - dýchání, arytmie nebo špatná dechová spolupráce; stabilizujte zobrazení a použijte **Clear / Auto**, pokud je dostupné.
- **Preset** - ověřte správnou aplikaci / kardiologický preset pro dané vyšetření.

Pokud je část obrazu v pořádku a jiná ne, upravte **TGC** pro danou hloubku. Při trvalých artefaktech zkontrolujte kabel sondy a znovu naneste gel.`,
    startExam: `Typický začátek kardiologického vyšetření na **Vivid E90** (detaily se řídí vaším pracovištěm):

1. **Vyberte pacienta / studii** z worklistu (nebo zaregistrujte nového pacienta).
2. Zvolte **kardiologickou aplikaci** a sondu, kterou budete používat (např. phased array).
3. **Připravte pacienta** - ideálně levý boční decubitus, EKG svody pokud používáte gating.
4. **Vyberte protokol nebo preset** (např. adult echo), aby odpovídala hloubka, škála a módy.
5. **Akvizice** - přiložte sondu, optimalizujte obraz a ukládejte smyčky i měření dle interního checklistu.

Používejte standardní sekvenci projekcí vašeho oddělení; nástroje **Help** nebo **protocol** v systému mohou zobrazit doporučené projekce.`,
    measureEf: `Na **Vivid E90** se ejekční frakce (EF) často měří nástroji jako **Auto EF**, které využívají **2D speckle-tracking** ve vhodných projekcích levé komory.

**Praktický postup (zjednodušeně):**

1. Získejte kvalitní **apikální** (nebo předepsanou) projekci s dobře viditelným endokardem.
2. Otevřete nástroj pro **EF / funkci LK**, který je dostupný v presetu (např. Auto EF).
3. Podle pokynů vymezte oblast levé komory a nechte algoritmus sledovat srdeční cyklus.
4. Zkontrolujte obkreslení a zobrazenou hodnotu **EF**; při špatném trackování akvizici opakujte.

Vždy postupujte podle metodiky vašeho pracoviště a pokynů výrobce pro konkrétní verzi softwaru.`,
  },
  de: {
    imageUnclear: `Häufige Gründe, warum das Bild auf Systemen wie **Vivid E90** unscharf oder verrauscht wirkt:

- **Sondenkontakt & Gel** - zu wenig Gel, Luft unter der Sonde oder Ablösen von der Haut während der Atmung.
- **Gain & Tiefe** - Gesamtgain zu niedrig (dunkel) oder zu hoch (überstrahlt); Tiefe tiefer als nötig eingestellt.
- **Fokus & Frequenz** - Fokuszone liegt nicht in der Region of Interest; bei Verfügbarkeit harmonisches Imaging testen.
- **Patient / Bewegung** - Atmung, Arrhythmie oder schlechtes Atemanhalten; Bild stabilisieren und ggf. **Clear / Auto** verwenden.
- **Preset** - prüfen, ob das richtige kardiale Preset für den Untersuchungstyp aktiv ist.

Wenn ein Bildbereich gut ist und ein anderer nicht, passen Sie die **TGC**-Regler für diese Tiefe an. Bei anhaltenden Artefakten Sondenkabel prüfen und neu gelen.`,
    startExam: `Typischer Start einer kardialen Untersuchung auf **Vivid E90** (Details gemäß Ihrem Workflow vor Ort):

1. **Patient / Studie auswählen** aus der Worklist (oder neuen Patienten anlegen).
2. **Kardiale Anwendung** und verwendete Sonde wählen (z. B. Phased Array).
3. **Patient vorbereiten** - möglichst Linksseitenlage, EKG-Elektroden bei EKG-Gating.
4. **Protokoll oder Preset** wählen (z. B. Adult Echo), damit Tiefe, Skala und Modi passen.
5. **Akquirieren** - Sonde positionieren, Bild optimieren, dann Loops und Messungen gemäß Labor-Checkliste speichern.

Nutzen Sie die Standardansichten Ihres Bereichs; **Help**- oder **Protocol**-Funktionen im System können empfohlene Ansichten anzeigen.`,
    measureEf: `Auf **Vivid E90** wird die Ejektionsfraktion (EF) oft mit Werkzeugen wie **Auto EF** gemessen, die einen **2D Speckle-Tracking**-Ansatz in geeigneten linksventrikulären Ansichten nutzen.

**Praktische Schritte (hohes Niveau):**

1. Gute **apikale** (oder vorgeschriebene) Ansicht mit klarer Endokardabgrenzung aufnehmen.
2. Das Quantifizierungswerkzeug für **EF / LV-Funktion** öffnen, das Ihr Preset bereitstellt (z. B. Auto EF).
3. Den Anweisungen folgen, um die LV-Region festzulegen; den Zyklus durch den Algorithmus tracken lassen.
4. Konturen und gemeldete **EF** prüfen; bei schlechtem Tracking Aufnahme wiederholen.

Beachten Sie immer die Messrichtlinien Ihrer Einrichtung und die Herstelleranweisungen für Ihre Softwareversion.`,
  },
};

export function getSuggestedQuestionLabels(locale) {
  const l = QUESTION_LABELS[locale] ? locale : 'en';
  return QUESTION_KEYS.map((key) => ({ key, label: QUESTION_LABELS[l][key] }));
}

export function getCannedAnswerForSuggestedQuestion(text, locale) {
  const q = String(text || '').trim().toLowerCase();
  if (!q) return null;
  const l = QUESTION_LABELS[locale] ? locale : 'en';
  for (const key of QUESTION_KEYS) {
    const labels = [QUESTION_LABELS[l][key], QUESTION_LABELS.en[key]];
    if (labels.some((label) => label.toLowerCase() === q)) {
      return ANSWERS[l][key] || ANSWERS.en[key];
    }
  }
  return null;
}
